import { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { HelpCircle } from 'lucide-react';
import api from '../../service/api';
import {
  FAIXAS_IDADE_CF, TESTES_CF, CORES_CLASSIFICACAO_CF,
  indiceFaixaIdadeCF, ehSexoMasculinoCF, referenciaCF, formatarFaixaCF, classificarCF,
} from '../../data/tabelasCapacidadeFuncional';
import { proximaEtapa, textoProximaEtapa } from '../../data/fluxoAvaliacao';
import '../../css/Anamnese.css';
import '../../css/Capacidade_funcional.css';
import '../../css/style.css';
import exemploIdosos from '../../images/exemplo-idosos.jpg';

const testePorChave = (chave) => TESTES_CF.find((t) => t.chave === chave);

// Tabela de valores esperados por sexo e faixa etária; destaca a célula do avaliado.
const TabelaReferencia = ({ teste, sexo, idade }) => {
  const idx = indiceFaixaIdadeCF(idade);
  const masc = ehSexoMasculinoCF(sexo);

  const linha = (rotulo, valores, destacaLinha) => (
    <tr className={destacaLinha ? 'linha-destaque-cf' : ''}>
      <td>{rotulo}</td>
      {valores.map((faixa, i) => (
        <td key={i} className={destacaLinha && i === idx ? 'celula-destaque-cf' : ''}>
          {formatarFaixaCF(faixa)}
        </td>
      ))}
    </tr>
  );

  return (
    <div>
      <table className="tabela-referencia-cf">
        <thead>
          <tr>
            <th>Idade</th>
            {FAIXAS_IDADE_CF.map((faixa) => <th key={faixa}>{faixa}</th>)}
          </tr>
        </thead>
        <tbody>
          {linha('Feminino', teste.referencias.feminino, !masc && idx >= 0)}
          {linha('Masculino', teste.referencias.masculino, masc && idx >= 0)}
        </tbody>
      </table>
      <p className="tabela-referencia-legenda">
        {idx < 0
          ? `Valores esperados em ${teste.unidade} — tabela válida para 60 a 94 anos.`
          : `Valores esperados em ${teste.unidade}. A célula destacada é a faixa esperada para o sexo e a idade do avaliado.`}
      </p>
    </div>
  );
};

// Selo com a classificação do valor informado dentro da faixa esperada do avaliado.
const ClassificacaoTeste = ({ teste, valor, sexo, idade }) => {
  const classe = classificarCF(teste, valor, sexo, idade);
  if (!classe) return null;
  const faixa = referenciaCF(teste, sexo, idade);
  return (
    <div className="classificacao-cf" style={{ borderColor: CORES_CLASSIFICACAO_CF[classe] }}>
      Classificação: <strong>{classe}</strong> (esperado: {formatarFaixaCF(faixa)} {teste.unidade})
    </div>
  );
};

const BlocoResultados = ({ chave, valor, sexo, idade, children }) => {
  const teste = testePorChave(chave);
  return (
    <div className="questions-right">
      <div className="question-item">
        <div className="question-title">Resultados</div>
        {children}
        <TabelaReferencia teste={teste} sexo={sexo} idade={idade} />
        <ClassificacaoTeste teste={teste} valor={valor} sexo={sexo} idade={idade} />
      </div>
    </div>
  );
};

const CampoResultado = ({ rotulo, name, valor, onChange }) => (
  <div className="campo-resultado-cf">
    <span>{rotulo}</span>
    <input type="text" name={name} value={valor} onChange={(e) => onChange(e.target.value)} />
  </div>
);

function TesteCapacidadeFuncional({ setActiveTab, aluno, evaluationId, setEvaluationId, evaluationData, updateEvaluationData }) {
  const [etapa, setEtapa] = useState(1);
  const [isNextModal, setIsNextModal] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const totalEtapas = 7;
  const porcentagemConclusao = (etapa / totalEtapas) * 100;

  const sexo = aluno?.sexo;
  const idade = aluno?.idade;
  const proxima = proximaEtapa(aluno?.profile, 'TesteCapFunc');

  const [flexaoCotovelo, setFlexaoCotovelo] = useState('');
  const [levantarCadeira, setLevantarCadeira] = useState('');
  const [testeMarcha, setTesteMarcha] = useState('');
  const [alcancarPes, setAlcancarPes] = useState('');
  const [levantarCaminhar, setLevantarCaminhar] = useState('');
  const [alcancarCostas, setAlcancarCostas] = useState('');

  const [horarioTeste1, setHorarioTeste1] = useState('');
  const [repousoBorg, setRepousoBorg] = useState('');
  const [repousoFR, setRepousoFR] = useState('');
  const [repousoSp0, setRepousoSp0] = useState('');
  const [repousoFC, setRepousoFC] = useState('');
  const [repousoPA, setRepousoPA] = useState('');

  const [horarioTeste2, setHorarioTeste2] = useState('');
  const [repousoBorg2, setRepousoBorg2] = useState('');
  const [repousoFR2, setRepousoFR2] = useState('');
  const [repousoSp02, setRepousoSp02] = useState('');
  const [repousoFC2, setRepousoFC2] = useState('');
  const [repousoPA2, setRepousoPA2] = useState('');

  const [distanciaTeste1, setDistanciaTeste1] = useState('');
  const [distanciaTeste2, setDistanciaTeste2] = useState('');
  const [paradas1, setParadas1] = useState('');
  const [paradas2, setParadas2] = useState('');

  useEffect(() => {
    const t = evaluationData?.testeCapacidadeFuncional;
    if (!t) return;

    if (t.flexaoCotovelo !== undefined) setFlexaoCotovelo(t.flexaoCotovelo);
    if (t.levantarCadeira !== undefined) setLevantarCadeira(t.levantarCadeira);
    if (t.testeMarcha !== undefined) setTesteMarcha(t.testeMarcha);
    if (t.alcancarPes !== undefined) setAlcancarPes(t.alcancarPes);
    if (t.levantarCaminhar !== undefined) setLevantarCaminhar(t.levantarCaminhar);
    if (t.alcancarCostas !== undefined) setAlcancarCostas(t.alcancarCostas);

    const c = t.caminhada6min || {};
    if (c.horarioTeste1 !== undefined) setHorarioTeste1(c.horarioTeste1);
    if (c.repousoBorg !== undefined) setRepousoBorg(c.repousoBorg);
    if (c.repousoFR !== undefined) setRepousoFR(c.repousoFR);
    if (c.repousoSp0 !== undefined) setRepousoSp0(c.repousoSp0);
    if (c.repousoFC !== undefined) setRepousoFC(c.repousoFC);
    if (c.repousoPA !== undefined) setRepousoPA(c.repousoPA);
    if (c.horarioTeste2 !== undefined) setHorarioTeste2(c.horarioTeste2);
    if (c.repousoBorg2 !== undefined) setRepousoBorg2(c.repousoBorg2);
    if (c.repousoFR2 !== undefined) setRepousoFR2(c.repousoFR2);
    if (c.repousoSp02 !== undefined) setRepousoSp02(c.repousoSp02);
    if (c.repousoFC2 !== undefined) setRepousoFC2(c.repousoFC2);
    if (c.repousoPA2 !== undefined) setRepousoPA2(c.repousoPA2);
    if (c.distanciaTeste1 !== undefined) setDistanciaTeste1(c.distanciaTeste1);
    if (c.distanciaTeste2 !== undefined) setDistanciaTeste2(c.distanciaTeste2);
    if (c.paradas1 !== undefined) setParadas1(c.paradas1);
    if (c.paradas2 !== undefined) setParadas2(c.paradas2);
  }, [evaluationData]);

  const avancarEtapa = () => setEtapa((prev) => (prev < totalEtapas ? prev + 1 : prev));
  const retrocederEtapa = () => setEtapa((prev) => (prev > 1 ? prev - 1 : prev));

  // O melhor desempenho entre as duas tentativas é o que vale para a classificação.
  const melhorDistancia = () => {
    const valores = [distanciaTeste1, distanciaTeste2]
      .map((x) => Number(String(x).replace(',', '.')))
      .filter((x) => Number.isFinite(x) && x > 0);
    return valores.length ? Math.max(...valores) : '';
  };

  const handleTesteCapacidadeFuncionalRegistrar = async () => {
    const payload = {
      testeCapacidadeFuncional: {
        flexaoCotovelo, levantarCadeira, testeMarcha,
        alcancarPes, levantarCaminhar, alcancarCostas,
        caminhada6min: {
          horarioTeste1, repousoBorg, repousoFR, repousoSp0, repousoFC, repousoPA,
          horarioTeste2, repousoBorg2, repousoFR2, repousoSp02, repousoFC2, repousoPA2,
          distanciaTeste1, distanciaTeste2, paradas1, paradas2,
        },
      },
      evaluationId
    };

    try {
      const response = await api.post(`/api/student/${aluno.id}/evaluation/teste-capacidade-funcional`, payload, { withCredentials: true });
      if (response.status === 200) {
        if (response.data.evaluationId && setEvaluationId) {
          if (!evaluationId) {
            if (!aluno.avaliacoesRealizadas) aluno.avaliacoesRealizadas = [];
            aluno.avaliacoesRealizadas.push({
              id: response.data.evaluationId,
              data: new Date().toISOString()
            });
          }
          setEvaluationId(response.data.evaluationId);
        }
        if (updateEvaluationData) {
          updateEvaluationData({ testeCapacidadeFuncional: payload.testeCapacidadeFuncional });
        }
        setIsNextModal(true);
      }
    } catch (error) {
      console.error('Erro ao salvar Teste de Capacidade Funcional:', error);
    }
  };

  // Coletas em repouso das duas tentativas do teste de 6 minutos, em uma tabela só.
  const linhaColeta = (rotulo, campos) => (
    <tr>
      <td>{rotulo}</td>
      {campos.map(([valor, setValor], i) => (
        <td key={i}>
          <input type="text" value={valor} onChange={(e) => setValor(e.target.value)} />
        </td>
      ))}
    </tr>
  );

  return (
    <div className="avaliacao-container">
      <div className="avaliacao-content">

        {/* ETAPA 1: Flexão e extensão de cotovelo */}
        {etapa === 1 && (
          <div className="questions-container">
            <div className="questions-left">
              <div className="question-item">
                <div className="question-title">Flexão e extensão de cotovelo</div>
                <div className="capacidade-funcional-protocolos">
                  <div className="capacidade-funcional-protocolos-itens">
                    Objetivo: Avaliar a força de membros superiores.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Procedimento: O avaliado deverá estar sentado em uma cadeira, com o tronco
                    encostado e com os pés totalmente apoiados no solo. O teste começa com o antebraço
                    perpendicular ao solo, segurando com a mão supinada, um halter de dois quilogramas
                    (2,27Kg) para mulheres e três quilogramas (3,63Kg) para homens na mão dominante.
                    Ao sinal do avaliador este deverá executar o maior número de flexões e extensões
                    possíveis em 30 segundos. Devido a dificuldade em encontrar pesos com esta
                    padronização utiliza-se halteres de 2Kg e 4Kg, respectivamente.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    É importante que o avaliador observe os seguintes itens:
                    <br />a) O participante deverá executar as flexões em seu máximo de amplitude;
                    <br />b) Ser incentivado a fazer o maior número de vezes possível;
                    <br />c) Deverá ser dada uma ou duas tentativas para adaptação antes do início do teste;
                    <br />d) Somente as tentativas completas serão anotadas.
                  </div>
                </div>
                <CampoResultado rotulo="Número de repetições:" name="flexaoCotovelo" valor={flexaoCotovelo} onChange={setFlexaoCotovelo} />
              </div>
            </div>
            <BlocoResultados chave="flexaoCotovelo" valor={flexaoCotovelo} sexo={sexo} idade={idade} />
          </div>
        )}

        {/* ETAPA 2: Levantar e sentar da cadeira */}
        {etapa === 2 && (
          <div className="questions-container">
            <div className="questions-left">
              <div className="question-item">
                <div className="question-title">Teste de levantar e sentar da cadeira em 30 segundos</div>
                <div className="capacidade-funcional-protocolos">
                  <div className="capacidade-funcional-protocolos-itens">
                    Objetivo: Avaliar a força de membros inferiores.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Procedimento: O avaliado sentado em uma cadeira sem braços, com 43cm de altura
                    aproximadamente e braços cruzados na frente do tórax, deverá ficar em pé e retornar
                    para a posição inicial sentada o maior número de vezes em 30 segundos.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    É importante que o avaliador observe os seguintes itens:
                    <br />a) O participante deverá sentar-se e encostar totalmente as costas no encosto da cadeira a cada tentativa;
                    <br />b) Deverá ser incentivado a fazer o maior número de vezes possível;
                    <br />c) Deverá ser dada uma ou duas tentativas para adaptação antes do início do teste;
                    <br />d) Somente as tentativas completas serão anotadas.
                  </div>
                </div>
                <CampoResultado rotulo="Número de repetições:" name="levantarCadeira" valor={levantarCadeira} onChange={setLevantarCadeira} />
              </div>
            </div>
            <BlocoResultados chave="levantarCadeira" valor={levantarCadeira} sexo={sexo} idade={idade} />
          </div>
        )}

        {/* ETAPA 3: Marcha estacionária */}
        {etapa === 3 && (
          <div className="questions-container">
            <div className="questions-left">
              <div className="question-item">
                <div className="question-title">Teste de marcha estacionária de 2 minutos</div>
                <div className="capacidade-funcional-protocolos">
                  <div className="capacidade-funcional-protocolos-itens">
                    Objetivo: Fornecer uma alternativa para o teste de resistência aeróbica.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Equipamento: Cronômetros, fita métrica e fita adesiva. Recomendações de segurança:
                    Os avaliados com problemas de equilíbrio devem ficar em pé próximo a uma parede,
                    porta ou cadeira e monitorados com atenção. Monitorar todos os avaliados com
                    atenção para sinais de esforço excessivo. Ao final do teste pedir para continuar
                    marchando durante mais de um minuto para recuperação.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Preparação: Começar marcando a altura mínima de elevação do joelho (ponto médio
                    entre a patela e a crista ilíaca).
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Procedimento: Ao comando o avaliado começa a marchar no lugar (sem correr),
                    completando tantas elevações quanto conseguir em 2 minutos. Quando a altura do
                    joelho não puder ser mantida, o avaliado é orientado a parar, ou parar e descansar
                    até que a forma apropriada possa ser readquirida.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Pontuação: O escore corresponde ao número de passos realizados em 2 minutos,
                    isto é, o número de vezes que a perna direita atinge a altura assinalada.
                  </div>
                </div>
                <CampoResultado rotulo="Número de passos:" name="testeMarcha" valor={testeMarcha} onChange={setTesteMarcha} />
              </div>
            </div>
            <BlocoResultados chave="testeMarcha" valor={testeMarcha} sexo={sexo} idade={idade} />
          </div>
        )}

        {/* ETAPA 4: Sentar e alcançar os pés */}
        {etapa === 4 && (
          <div className="questions-container">
            <div className="questions-left">
              <div className="question-item">
                <div className="question-title">Teste de sentar e alcançar os pés</div>
                <div className="capacidade-funcional-protocolos">
                  <div className="capacidade-funcional-protocolos-itens">
                    Objetivo: Avaliar a flexibilidade de membros inferiores
                    (principalmente os músculos posteriores da coxa).
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Equipamento: Cadeira com a altura do assento de 43,18cm e com as
                    pernas inclinadas para frente, e uma régua de 50 centímetros.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Precauções de segurança: Colocar a cadeira contra a parede para que não escorregue
                    durante o teste. Lembrar os avaliados de expirar à medida que se curvam para frente e
                    de evitar impulsos. Os avaliados devem estender o braço até o ponto de ligeiro
                    desconforto, nunca até o ponto de dor. Não aplicar o teste em pessoas com
                    osteoporose grave ou que sentem dor ao curvar-se para frente.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Procedimento: O avaliado senta-se na beirada da cadeira, a dobra entre a linha
                    inguinal e os glúteos devem coincidir com a beirada da cadeira. Uma das pernas
                    fica fletida com o pé no chão. A outra é estendida o máximo possível na frente do
                    quadril, com o calcanhar no chão e pé fletido em aproximadamente 90º.
                  </div>
                </div>
                <CampoResultado rotulo="Score:" name="alcancarPes" valor={alcancarPes} onChange={setAlcancarPes} />
              </div>
            </div>
            <BlocoResultados chave="alcancarPes" valor={alcancarPes} sexo={sexo} idade={idade}>
              <img className="tabela-flexao-cotovelo" src={exemploIdosos} alt="Exemplo de execução do teste" />
            </BlocoResultados>
          </div>
        )}

        {/* ETAPA 5: Levantar e caminhar */}
        {etapa === 5 && (
          <div className="questions-container">
            <div className="questions-left">
              <div className="question-item">
                <div className="question-title">Levantar e caminhar</div>
                <div className="capacidade-funcional-protocolos">
                  <div className="capacidade-funcional-protocolos-itens">
                    Objetivo: Avaliar a agilidade, o equilíbrio dinâmico e possível risco de queda.
                    Equipamento: Cronômetro, cadeira com altura do assento de aproximadamente 43cm, trena e cone.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Recomendações de segurança: Indivíduos mais frágeis requerem atenção especial no
                    momento de sentar e levantar da cadeira. Um dos assistentes deve colocar-se entre a
                    cadeira e o cone para ajudar os participantes em caso de desequilíbrio.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Preparação: A cadeira deve ser posicionada contra a parede, voltada diretamente
                    para um cone colocado à distância de 2,44 metros medida a partir da parte anterior do
                    cone até um ponto no chão situado no mesmo nível da parte da frente da cadeira.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Procedimento: O avaliado começa em uma posição sentada na cadeira com uma postura ereta,
                    mãos nas coxas e os pés no chão com um pé levemente na frente do outro. Ao comando o
                    avaliado deve levantar-se da cadeira, caminhar o mais rápido possível até o cone,
                    contorná-lo e voltar para a cadeira, sentando-se novamente.
                  </div>
                </div>
                <CampoResultado rotulo="Score (segundos):" name="levantarCaminhar" valor={levantarCaminhar} onChange={setLevantarCaminhar} />
              </div>
            </div>
            <BlocoResultados chave="levantarCaminhar" valor={levantarCaminhar} sexo={sexo} idade={idade}>
              <div className="capacidade-funcional-protocolos">
                <div className="capacidade-funcional-protocolos-itens">
                  Acionar o cronômetro ao iniciar e pará-lo no exato instante em que a pessoa sentar na cadeira outra vez.
                  O avaliador demonstra o teste, e após o avaliado deve ensaiar o teste uma vez para praticar e,
                  então, realizar duas tentativas.
                </div>
                <div className="capacidade-funcional-protocolos-itens">
                  Pontuação: Registra o tempo dos dois escores do teste para o décimo de segundo mais
                  próximo, sendo o melhor tempo considerado para avaliar o desempenho.
                  O teste é considerado normal quando o tempo do percurso for inferior a 10 segundos.
                  Se o tempo estiver entre 10 e 19 segundos, considera-se que o idoso apresenta risco
                  moderado de queda, sendo este risco aumentado quando o tempo obtido for de 20 segundos ou mais.
                </div>
              </div>
            </BlocoResultados>
          </div>
        )}

        {/* ETAPA 6: Alcançar atrás das costas */}
        {etapa === 6 && (
          <div className="questions-container">
            <div className="questions-left">
              <div className="question-item">
                <div className="question-title">Teste de alcançar atrás das costas</div>
                <div className="capacidade-funcional-protocolos">
                  <div className="capacidade-funcional-protocolos-itens">
                    Objetivo: Avaliar a flexibilidade dos membros superiores (ombro).
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Equipamento: Régua de 50 centímetros.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Recomendações de segurança: Interromper o teste se o participante sentir
                    dor. Lembrá-los de continuar respirando enquanto se alongam e de
                    evitar impulso ou movimentos rápidos.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Procedimento: Em pé, o avaliado coloca a mão preferida sobre o mesmo
                    ombro, com a palma e os dedos estendidos, tentando alcançar a parte
                    central das costas. A outra mão deve ser colocada para trás, com a palma da mão para cima,
                    tentando fazer com que os dedos de ambas as mãos se toquem ou se
                    sobreponham. Seguindo a demonstração, o avaliado determina a mão preferida e são
                    feitas duas tentativas de aprendizagem, seguidas pelo teste (2 tentativas).
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Pontuação: Medir a distância de sobreposição ou a distância entre as pontas
                    dos dedos médios, indicando o melhor score.
                  </div>
                </div>
                <CampoResultado rotulo="Score:" name="alcancarCostas" valor={alcancarCostas} onChange={setAlcancarCostas} />
              </div>
            </div>
            <BlocoResultados chave="alcancarCostas" valor={alcancarCostas} sexo={sexo} idade={idade}>
              <div className="capacidade-funcional-protocolos">
                <div className="capacidade-funcional-protocolos-itens">
                  Atribuir valor negativo se os dedos não se tocarem, escore zero se os dedos
                  médios se tocarem ligeiramente e escore positivo se os dedos médios se
                  sobrepuserem.
                </div>
              </div>
            </BlocoResultados>
          </div>
        )}

        {/* ETAPA 7: Caminhada de 6 minutos */}
        {etapa === 7 && (
          <div className="questions-container">
            <div className="questions-left">
              <div className="question-item">
                <div className="question-title">Teste de caminhada de 6 minutos</div>
                <div className="capacidade-funcional-protocolos">
                  <div className="capacidade-funcional-protocolos-itens">
                    Objetivo: Avaliar a resistência aeróbica.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Equipamento: Trena, cronômetros, 4 cones, fita adesiva e ficha de anotação.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Recomendações de segurança: Selecionar uma área bem iluminada, com
                    superfície não escorregadia e nivelada. Colocar cadeiras em vários pontos
                    do lado de fora da área de percurso. Interromper o teste ao sinal de esforço
                    excessivo.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Organização do percurso: Armar um percurso de 50 metros, marcado em
                    segmentos de 5 metros, com giz ou fita.
                  </div>
                  <div className="capacidade-funcional-protocolos-itens">
                    Procedimento: Ao sinal os participantes caminham o mais rápido possível
                    (sem correr) em volta do percurso quantas vezes eles puderem, dentro do
                    limite de 6 minutos. Durante o teste os participantes podem parar e descansar, e
                    depois voltar a caminhar. Realizar um minuto de recuperação, através de caminhada.
                  </div>
                </div>

                <div className="resultados-cf-grid margin-top-15">
                  <div className="resultados-cf-coluna">
                    <div className="subtitulo-cf">Primeiro teste</div>
                    <CampoResultado rotulo="Distância (m):" name="distanciaTeste1" valor={distanciaTeste1} onChange={setDistanciaTeste1} />
                    <CampoResultado rotulo="Paradas:" name="paradas1" valor={paradas1} onChange={setParadas1} />
                  </div>
                  <div className="resultados-cf-coluna">
                    <div className="subtitulo-cf">Segundo teste</div>
                    <CampoResultado rotulo="Distância (m):" name="distanciaTeste2" valor={distanciaTeste2} onChange={setDistanciaTeste2} />
                    <CampoResultado rotulo="Paradas:" name="paradas2" valor={paradas2} onChange={setParadas2} />
                  </div>
                </div>
              </div>
            </div>

            <div className="questions-right">
              <div className="question-item">
                <div className="question-title">Coletas em repouso</div>
                <table className="tabela-coleta-cf">
                  <thead>
                    <tr>
                      <th>Tentativa</th>
                      <th>Horário</th>
                      <th>Borg</th>
                      <th>FR</th>
                      <th>Sp0</th>
                      <th>FC</th>
                      <th>PA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhaColeta('Primeiro teste', [
                      [horarioTeste1, setHorarioTeste1], [repousoBorg, setRepousoBorg], [repousoFR, setRepousoFR],
                      [repousoSp0, setRepousoSp0], [repousoFC, setRepousoFC], [repousoPA, setRepousoPA],
                    ])}
                    {linhaColeta('Segundo teste', [
                      [horarioTeste2, setHorarioTeste2], [repousoBorg2, setRepousoBorg2], [repousoFR2, setRepousoFR2],
                      [repousoSp02, setRepousoSp02], [repousoFC2, setRepousoFC2], [repousoPA2, setRepousoPA2],
                    ])}
                  </tbody>
                </table>

                <div className="question-title margin-top-15">Resultados</div>
                <TabelaReferencia teste={testePorChave('caminhada6min')} sexo={sexo} idade={idade} />
                <ClassificacaoTeste teste={testePorChave('caminhada6min')} valor={melhorDistancia()} sexo={sexo} idade={idade} />
              </div>
            </div>
          </div>
        )}

        <div className="pagination-footer">
          <div className="sub-pagination-footer">
            <div className="sub-pagination-buttons">
              <button className="next-prev-button" type="button" onClick={retrocederEtapa} disabled={etapa === 1}>
                Voltar
              </button>
              {etapa < totalEtapas ? (
                <button className="next-prev-button" type="button" onClick={avancarEtapa}>
                  Avançar
                </button>
              ) : (
                <button className="next-prev-button" type="button" onClick={handleTesteCapacidadeFuncionalRegistrar}>
                  Registrar
                </button>
              )}
            </div>
            <div className="progress-container">
              <div className="progress-bar" style={{ width: `${porcentagemConclusao}%` }}></div>
            </div>
          </div>

          <div className="info-box-container" onMouseEnter={() => setShowInfo(true)} onMouseLeave={() => setShowInfo(false)}>
            <div className="info-button">
              <HelpCircle size={20} />
              Mais informações
            </div>
            {showInfo && (
              <div className="info-box-content">
                <p>
                  A bateria de testes de capacidade funcional avalia, de forma prática e padronizada, os componentes físicos necessários para a realização das atividades do dia a dia do idoso: força de membros superiores e inferiores, flexibilidade, agilidade, equilíbrio dinâmico e resistência aeróbica.
                  <br /><br />
                  Cada teste possui um protocolo próprio de execução e tabelas de referência por sexo e faixa etária, permitindo comparar o desempenho do avaliado com os valores esperados para sua idade.
                  <br /><br />
                  Os resultados orientam a prescrição do treinamento, o acompanhamento da evolução e a identificação precoce de perdas funcionais e de risco de queda.
                </p>
              </div>
            )}
          </div>
        </div>

        <Modal
          isOpen={isNextModal}
          onRequestClose={() => setIsNextModal(false)}
          contentLabel="Confirmacao"
          className="modal-next"
          overlayClassName="popup-overlay"
        >
          <p className="popupNext-h1">Teste de Capacidade Funcional finalizado!</p>
          <p className="popupNext-h2">{textoProximaEtapa(proxima)}</p>
          <div className="sub-pagination-buttons">
            <button className="next-prev-button" type="button" onClick={() => proxima && setActiveTab(proxima.chave)}>Sim</button>
            <button className="next-prev-button" type="button" onClick={() => setIsNextModal(false)}>Não</button>
          </div>
        </Modal>
      </div>
    </div>
  );
}

export default TesteCapacidadeFuncional;
